/**
 * Capture-Socket-Client (DOM-frei, läuft im Browser und in Node ≥ 22 mit
 * globalem WebSocket). Sendet `start`, wartet auf `started`, schickt
 * Int16-Frames und `speech`/`level`; Reconnect mit Backoff und erneutem
 * `start` (gleiche source.id) nach Doc 04 §2.4 / Doc 13 §3.2.
 */
import type { CaptureStart, CaptureStarted } from '@voice/protocol';
import { backoffDelay, FATAL_CLOSE_CODES, FATAL_ERROR_CODES } from './backoff.js';

export type CaptureSocketState = 'idle' | 'connecting' | 'streaming' | 'reconnecting' | 'stopped' | 'error';

export interface CaptureSocketOptions {
  url: string;
  /** Wird bei jedem (Re-)Connect neu erzeugt. */
  startMessage: () => CaptureStart;
  WebSocketImpl?: typeof WebSocket;
  /** max. Bytes im WebSocket-Puffer, bevor lokal gepuffert/verworfen wird (Default 1 s Audio) */
  maxBufferedBytes?: number;
  /** max. lokal wartende Frames, ältester fliegt zuerst (Default 13 ≈ 1 s) */
  maxQueuedFrames?: number;
  /** Mindestabstand zwischen `level`-Nachrichten (Default 200 ms = 5/s) */
  levelIntervalMs?: number;
  onState?: (state: CaptureSocketState, detail?: { code?: string; message?: string }) => void;
  onStarted?: (msg: CaptureStarted, reconnect: boolean) => void;
  onError?: (code: string, message: string, fatal: boolean) => void;
  now?: () => number;
}

export class CaptureSocket {
  state: CaptureSocketState = 'idle';
  dropped = 0;
  sent = 0;
  private ws: WebSocket | null = null;
  private attempt = 0;
  private everStarted = false;
  private stopping = false;
  private fatal = false;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private queue: ArrayBuffer[] = [];
  private lastLevelAt = -Infinity;
  private startedResolve: ((m: CaptureStarted) => void) | null = null;
  private startedReject: ((e: Error) => void) | null = null;

  constructor(private readonly opts: CaptureSocketOptions) {}

  /** Verbindet und resolved beim ersten `started` (rejected bei fatalem Fehler). */
  connect(): Promise<CaptureStarted> {
    this.stopping = false;
    this.fatal = false;
    return new Promise((resolve, reject) => {
      this.startedResolve = resolve;
      this.startedReject = reject;
      this.open();
    });
  }

  get isStreaming(): boolean {
    return this.state === 'streaming';
  }

  sendFrame(pcm: Int16Array): void {
    if (this.state !== 'streaming' || !this.ws) {
      this.dropped++;
      return;
    }
    const buf = pcm.byteOffset === 0 && pcm.byteLength === pcm.buffer.byteLength ? (pcm.buffer as ArrayBuffer) : pcm.slice().buffer;
    this.queue.push(buf);
    const maxQ = this.opts.maxQueuedFrames ?? 13;
    while (this.queue.length > maxQ) {
      this.queue.shift();
      this.dropped++;
    }
    this.drain();
  }

  sendSpeech(state: 'start' | 'end', t: number): void {
    this.sendJson({ v: 2, type: 'speech', state, t: Math.max(0, Math.round(t)) });
  }

  sendLevel(rms: number): void {
    const now = (this.opts.now ?? Date.now)();
    if (now - this.lastLevelAt < (this.opts.levelIntervalMs ?? 200)) return;
    this.lastLevelAt = now;
    this.sendJson({ v: 2, type: 'level', rms: Math.min(1, Math.max(0, rms)) });
  }

  /** Sendet `stop`, wartet auf das Schließen durch das Gateway (≤ timeoutMs). */
  async stop(timeoutMs = 4000): Promise<void> {
    this.stopping = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    const ws = this.ws;
    if (!ws || ws.readyState !== ws.OPEN) {
      this.setState('stopped');
      ws?.close();
      return;
    }
    this.drain();
    const closed = new Promise<void>((resolve) => ws.addEventListener('close', () => resolve(), { once: true }));
    ws.send(JSON.stringify({ v: 2, type: 'stop' }));
    await Promise.race([closed, new Promise<void>((r) => setTimeout(r, timeoutMs))]);
    ws.close(); // no-op, falls schon geschlossen
    this.setState('stopped');
  }

  /** Verbindung hart trennen (Tests: simulierter Netzabbruch → Reconnect). */
  dropConnection(): void {
    this.ws?.close(4000, 'client drop');
  }

  private sendJson(msg: object): void {
    if (this.state !== 'streaming' || !this.ws) return;
    this.drain();
    this.ws.send(JSON.stringify(msg));
  }

  private drain(): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== ws.OPEN) return;
    const max = this.opts.maxBufferedBytes ?? 32000;
    while (this.queue.length > 0 && ws.bufferedAmount < max) {
      ws.send(this.queue.shift() as ArrayBuffer);
      this.sent++;
    }
  }

  private open(): void {
    const WS = this.opts.WebSocketImpl ?? WebSocket;
    this.setState(this.everStarted ? 'reconnecting' : 'connecting');
    const ws = new WS(this.opts.url);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;
    ws.addEventListener('open', () => {
      ws.send(JSON.stringify(this.opts.startMessage()));
    });
    ws.addEventListener('message', (ev: MessageEvent) => {
      if (typeof ev.data !== 'string') return;
      let msg: { type?: string; code?: string; message?: string };
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (msg.type === 'started') {
        const reconnect = this.everStarted;
        this.everStarted = true;
        this.attempt = 0;
        this.queue = []; // Ringpuffer verwerfen: das Gateway stempelt die Lücke (Doc 04 §2.4)
        this.setState('streaming');
        this.opts.onStarted?.(msg as CaptureStarted, reconnect);
        this.startedResolve?.(msg as CaptureStarted);
        this.startedResolve = null;
        this.startedReject = null;
      } else if (msg.type === 'error') {
        const code = msg.code ?? 'internal';
        const fatal = FATAL_ERROR_CODES.has(code);
        if (fatal) this.fatal = true;
        this.opts.onError?.(code, msg.message ?? '', fatal);
        if (fatal) {
          this.setState('error', { code, message: msg.message ?? '' });
          this.startedReject?.(Object.assign(new Error(msg.message ?? code), { code }));
          this.startedReject = null;
          this.startedResolve = null;
        }
      } else if (msg.type === 'ping') {
        ws.send(JSON.stringify({ v: 2, type: 'pong' }));
      }
    });
    ws.addEventListener('close', (ev: CloseEvent) => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.stopping) {
        this.setState('stopped');
        return;
      }
      if (this.fatal || FATAL_CLOSE_CODES.has(ev.code)) {
        if (this.state !== 'error') this.setState('error', { code: `close_${ev.code}`, message: ev.reason });
        this.startedReject?.(Object.assign(new Error(ev.reason || `geschlossen (${ev.code})`), { code: `close_${ev.code}` }));
        this.startedReject = null;
        return;
      }
      const delay = backoffDelay(this.attempt++);
      this.setState('reconnecting');
      this.retryTimer = setTimeout(() => this.open(), delay);
    });
    ws.addEventListener('error', () => {
      // close folgt; dort wird entschieden
    });
  }

  private setState(s: CaptureSocketState, detail?: { code?: string; message?: string }): void {
    if (this.state === s && !detail) return;
    this.state = s;
    this.opts.onState?.(s, detail);
  }
}
